import type React from "react";
import { useState } from "react";

import { useLocation, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import ThemeToggle from "@/components/theme-toggle";
import {
  FileText,
  ClipboardList,
  ClipboardCheck,
  ListChecks,
  BuildingIcon,
  Briefcase,
  ShieldAlert,
  ShieldCheck,
  MapIcon,
} from "lucide-react";

const menuGroups = [
  {
    title: "Audit Cycle",
    items: [
      { href: "/audit-plans", label: "Audit Plans", icon: FileText },
      { href: "/engagements", label: "Engagements", icon: Briefcase },
      { href: "/findings", label: "Findings", icon: ClipboardCheck },
      { href: "/action-plans", label: "Action Plans", icon: ListChecks },
    ],
  },
  {
    title: "Governance",
    items: [
      { href: "/risks", label: "Risks", icon: ShieldAlert },
      { href: "/controls", label: "Controls", icon: ShieldCheck },
      { href: "/audit-universe", label: "Audit Universe", icon: MapIcon },
    ],
  },
];

export default function AppSidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const [state] = useState<"expanded" | "collapsed">("expanded");
  const [searchTerm, _setSearchTerm] = useState("");

  const filteredMenuGroups = menuGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    }))
    .filter((group) => group.items.length > 0);

  const defaultOpen = menuGroups.findIndex((group) =>
    group.items.some((item) => pathname.startsWith(item.href)),
  );

  return (
    <TooltipProvider>
      <Sidebar collapsible="icon" side="left" variant="sidebar" className="flex flex-col">
        <SidebarHeader className="p-4 flex items-center gap-2.5">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <BuildingIcon className="w-7 h-7 text-primary flex-shrink-0" />
            {state === "expanded" && (
              <h1 className="text-xl font-bold text-primary whitespace-nowrap">
                Audit Platform
              </h1>
            )}
          </Link>
        </SidebarHeader>

        <SidebarContent className="flex-1 overflow-y-auto">
          {state === "expanded" ? (
            <Accordion
              type="single"
              collapsible
              defaultValue={`item-${defaultOpen > -1 ? defaultOpen : 0}`}
              className="w-full px-2"
            >
              {filteredMenuGroups.map((group, groupIndex) => (
                <AccordionItem
                  value={`item-${groupIndex}`}
                  key={group.title}
                  className="border-b-0"
                >
                  <AccordionTrigger className="py-2 px-2 text-sm font-semibold text-muted-foreground hover:no-underline hover:bg-muted/50 rounded-md [&[data-state=open]>svg]:rotate-180">
                    {group.title}
                  </AccordionTrigger>
                  <AccordionContent className="pt-1 pb-0">
                    <div className="flex flex-col space-y-1">
                      {group.items.map((item) => (
                        <Button
                          key={item.href}
                          asChild
                          variant={pathname.startsWith(item.href) ? "secondary" : "ghost"}
                          className="justify-start h-9"
                        >
                          <Link to={item.href} className="flex items-center gap-3">
                            <item.icon className="h-5 w-5" />
                            <span className="truncate">{item.label}</span>
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="flex flex-col items-center space-y-2 px-2">
              {menuGroups
                .flatMap((group) => group.items)
                .map((item) => (
                  <Tooltip key={item.href}>
                    <TooltipTrigger asChild>
                      <Button
                        asChild
                        variant={pathname.startsWith(item.href) ? "secondary" : "ghost"}
                        className="w-10 h-10 p-0"
                      >
                        <Link to={item.href}>
                          <item.icon className="h-5 w-5" />
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" align="center">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                ))}
            </div>
          )}
        </SidebarContent>

        <SidebarFooter className="p-3 space-y-3 mt-auto border-t">
          <div
            className={cn(
              "flex items-center",
              state === "expanded" ? "justify-between" : "justify-center flex-col gap-2",
            )}
          >
            <ThemeToggle />
          </div>
        </SidebarFooter>
      </Sidebar>
    </TooltipProvider>
  );
}
